import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useFormContext, Controller, UseFormSetValue } from 'react-hook-form'
import { Form, Button } from 'react-bootstrap'
import { CompanySearchModal } from '../CompanySearchModal'
import { ContactCompanySearchResult, DoorSearchResult } from 'models/companies'
import { FaTimes, FaEdit } from 'react-icons/fa'
import { PiMagnifyingGlass } from 'react-icons/pi'
import apiService, { ApiService } from 'services/api'
import { checkContactEmail } from 'services/samplesApi'
import { InitialDoorData } from '../SampleRequestFormModal'
import { ContactResponse } from 'models/contacts'
import Select from 'components/FormUtils/Select'
import CreatableSelect from 'components/FormUtils/CreatableSelect'
import { TextInput } from 'components/FormUtils/TextInput'
import { PhoneInput } from 'components/FormUtils/PhoneInput'
import ReactSelectWrapper from 'components/FormUtils/ReactSelectWrapper'
import FbButton from 'components/FbUI/FbButton'
import { cn } from 'components/UI/cn'
import { US_STATES } from 'utils/us_states'
import { US_CITIES } from 'utils/us_cities'

/**
 * Utility: Fetch company data with HQ address from chain if available
 */
async function fetchCompanyWithHQAddress(
  companyId: number,
  api: ApiService
): Promise<ContactCompanySearchResult> {
  const company = await api.getCompany(companyId)

  let hqCity: string | undefined
  let hqState: string | undefined

  // If company has a chain, fetch chain data to get HQ address
  if (company.chain) {
    try {
      const chainData = await api.getChain(company.chain)
      hqCity = chainData.hq_city1 || undefined
      hqState = chainData.hq_state1 || undefined
    } catch (err) {
      console.error('Error fetching chain data:', err)
    }
  }

  return {
    contact_company_id: companyId,
    name: company.name,
    domain: company.domain || '',
    city: hqCity || company.city || undefined,
    state: hqState || company.state || undefined,
    address_line1: company.address || undefined,
    zipcode: company.zipcode || undefined,
  }
}

/**
 * Utility: Fetch door proxy data and format it for the form.
 * Note: For restaurant doors opened from door pages, initialDoorData should be
 * passed instead to avoid redundant API calls. This function is only used as
 * a fallback for cases like editing existing sample requests.
 */
async function fetchDoorData(
  doorProxyId: number,
  companyId: number,
  companyName: string,
  api: ApiService
): Promise<DoorSearchResult> {
  const door = await api.getDoorProxy(doorProxyId)

  return {
    id: door.id,
    item_id: `door_${door.id}`,
    name: door.name || door.domain || `Door ${door.id}`,
    address: door.address || undefined,
    city: door.city || undefined,
    state: door.state || undefined,
    zipcode: door.zipcode || undefined,
    door_proxy_id: door.id,
    chain_id: undefined,
    chain_name: companyName,
    chain_proxy_id: companyId,
    type: undefined,
    domain: door.domain || undefined,
  }
}

/**
 * Utility: Set multiple form fields from data object
 */
function setFormFieldsFromData<T extends Record<string, any>>(
  data: T,
  fieldMapping: Partial<Record<keyof T, string>>,
  setValue: UseFormSetValue<any>
): void {
  Object.entries(fieldMapping).forEach(([dataKey, formKey]) => {
    const value = data[dataKey as keyof T]
    if (value) {
      setValue(formKey as string, value, { shouldValidate: false })
    }
  })
}

// Tailwind-based component classes
const Label = ({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) => (
  <Form.Label className={`font-semibold mb-2 ${className}`}>
    {children}
  </Form.Label>
)

const ErrorText = ({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) => <div className={`text-red-600 text-sm mt-1 ${className}`}>{children}</div>

const CompanyDisplay = ({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) => (
  <div
    className={`flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md mb-2 ${className}`}
  >
    {children}
  </div>
)

const CompanyInfo = ({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) => <div className={`flex-1 ${className}`}>{children}</div>

const CompanyName = ({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) => (
  <div className={`font-semibold text-gray-900 ${className}`}>{children}</div>
)

const CompanyDetails = ({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) => (
  <div className={`text-sm text-gray-600 mt-1 ${className}`}>{children}</div>
)

const SkeletonBox = ({ className = '' }: { className?: string }) => (
  <div
    className={`bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-pulse rounded-md ${className}`}
  />
)

const SkeletonLabel = ({ className = '' }: { className?: string }) => (
  <SkeletonBox className={`h-5 w-30 mb-2 ${className}`} />
)

const SkeletonInput = ({ className = '' }: { className?: string }) => (
  <SkeletonBox className={`h-10 w-full ${className}`} />
)

interface OrderDetailsStepProps {
  /** Pre-loaded door data to skip redundant API calls */
  initialDoorData?: InitialDoorData
  /** Callback to signal whether the Next button should be blocked */
  onEmailValidationChange?: (blocked: boolean) => void
}

export function OrderDetailsStep({
  initialDoorData,
  onEmailValidationChange,
}: OrderDetailsStepProps) {
  const {
    control,
    watch,
    getValues,
    setValue,
    trigger,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext()

  const api = apiService()
  const [isCheckingEmail, setIsCheckingEmail] = useState(false)
  const [emailExists, setEmailExists] = useState(false)
  const lastCheckedEmail = useRef<string>('')

  useEffect(() => {
    onEmailValidationChange?.(isCheckingEmail || emailExists)
  }, [isCheckingEmail, emailExists, onEmailValidationChange])

  const watchEmail = watch('contact_email')
  useEffect(() => {
    const trimmed = (watchEmail || '').trim().toLowerCase()
    if (trimmed !== lastCheckedEmail.current) {
      setEmailExists(false)
    }
  }, [watchEmail])

  const [showCompanySearch, setShowCompanySearch] = useState(false)
  const [selectedCompanyData, setSelectedCompanyData] =
    useState<ContactCompanySearchResult | null>(null)
  const [selectedDoorData, setSelectedDoorData] =
    useState<DoorSearchResult | null>(null)
  const [useManualEntry, setUseManualEntry] = useState(false)
  const [, setSelectedContactIdForDropdown] = useState<number | null>(null)

  const companyId = watch('contact_company_id')
  const chainId = watch('chain_id')

  useEffect(() => {
    lastCheckedEmail.current = ''
    setEmailExists(false)
    clearErrors('contact_email')
  }, [companyId, clearErrors])
  const doorProxyId = watch('door_proxy_id')
  const watchState = watch('shipping_state')

  // State options for dropdown
  const stateOptions = US_STATES

  // City options filtered by selected state
  const cityOptions = useMemo(() => {
    const state = watchState
    if (!state) return []
    return US_CITIES.filter((city) => city.value.endsWith(`_${state}`)).map(
      (city) => ({
        label: city.label.split(',')[0],
        value: city.label.split(',')[0],
      })
    )
  }, [watchState])

  // Determine if a company is selected (either via contact_company_id or chain_id)
  const hasCompanySelected =
    companyId > 0 || (chainId !== null && chainId !== undefined)

  // Set initial display data immediately when initialDoorData is provided
  // This allows the selected location card to show without waiting for API calls
  useEffect(() => {
    if (
      initialDoorData?.companyName &&
      companyId > 0 &&
      doorProxyId &&
      !selectedCompanyData
    ) {
      // Set initial company data for immediate display
      setSelectedCompanyData({
        contact_company_id: companyId,
        name: initialDoorData.companyName,
        domain: '',
        city: initialDoorData.city,
        state: initialDoorData.state,
      })
      // Set door data from initialDoorData
      setSelectedDoorData({
        id: doorProxyId,
        item_id: `door_${doorProxyId}`,
        name: initialDoorData.name,
        address: initialDoorData.address,
        city: initialDoorData.city,
        state: initialDoorData.state,
        zipcode: initialDoorData.zipcode,
        door_proxy_id: doorProxyId,
        chain_id: undefined,
        chain_name: initialDoorData.companyName,
        chain_proxy_id: companyId,
        type: undefined,
        domain: undefined,
      })
    }
  }, [initialDoorData, companyId, doorProxyId, selectedCompanyData])

  // Load full company data when form is pre-filled with initialCompanyId
  // Skip if initialDoorData was provided (we already have what we need for display)
  useEffect(() => {
    // Only load if we have a company ID but no selectedCompanyData yet
    // and we don't have initialDoorData (which means display data is already set)
    if (
      companyId > 0 &&
      !selectedCompanyData &&
      !initialDoorData?.companyName
    ) {
      fetchCompanyWithHQAddress(companyId, api)
        .then(async (companyData) => {
          setSelectedCompanyData(companyData)

          // If initialDoorData is provided, use it directly (already pre-loaded from page)
          // This avoids redundant API calls for door/restaurant data
          if (initialDoorData && doorProxyId) {
            // Create door data from initialDoorData for display
            setSelectedDoorData({
              id: doorProxyId,
              item_id: `door_${doorProxyId}`,
              name: initialDoorData.name,
              address: initialDoorData.address,
              city: initialDoorData.city,
              state: initialDoorData.state,
              zipcode: initialDoorData.zipcode,
              door_proxy_id: doorProxyId,
              chain_id: undefined,
              chain_name: companyData.name,
              chain_proxy_id: companyId,
              type: undefined,
              domain: undefined,
            })
            // Shipping address is already set from SampleRequestFormModal reset()
            // No need to set it again here
          } else if (doorProxyId) {
            // No initialDoorData - fetch door data the old way
            try {
              const doorData = await fetchDoorData(
                doorProxyId,
                companyId,
                companyData.name,
                api
              )
              setSelectedDoorData(doorData)
              setValue('door_proxy_id', doorData.door_proxy_id, {
                shouldValidate: false,
              })

              // Override shipping address with door's address if available
              // Door address is more specific than company HQ
              if (doorData.address) {
                setValue('shipping_address_line1', doorData.address, {
                  shouldValidate: false,
                })
              }
              if (doorData.city) {
                setValue('shipping_city', doorData.city, {
                  shouldValidate: false,
                })
              }
              if (doorData.state) {
                setValue('shipping_state', doorData.state, {
                  shouldValidate: false,
                })
              }
              if (doorData.zipcode) {
                setValue('shipping_zip', doorData.zipcode, {
                  shouldValidate: false,
                })
              }
            } catch (err) {
              console.error('Error loading door data:', err)
            }
          } else {
            // No door - prefill shipping address from company HQ data
            setFormFieldsFromData(
              companyData,
              {
                address_line1: 'shipping_address_line1',
                city: 'shipping_city',
                state: 'shipping_state',
                zipcode: 'shipping_zip',
              },
              setValue
            )
          }

          // Trigger validation after all values are set
          trigger(['contact_company_id', 'chain_id'])
        })
        .catch((err) => {
          console.error('Error loading company data:', err)
        })
    }
  }, [
    companyId,
    doorProxyId,
    selectedCompanyData,
    api,
    setValue,
    trigger,
    initialDoorData,
  ])

  // Fetch contacts for the selected company
  const { data: contactsResponse, isLoading: isLoadingContacts } =
    api.useGetContactsList(
      companyId > 0 ? { company: companyId } : {},
      undefined,
      companyId > 0
    )

  const contacts = contactsResponse?.results || []

  // Transform contacts into Select options
  const contactOptions = useMemo(() => {
    return contacts.map((contact: ContactResponse) => ({
      label: `${contact.first_name} ${contact.last_name}${contact.starred ? ' ⭐' : ''}${contact.email_preferred || contact.email_work ? ` - ${contact.email_preferred || contact.email_work}` : ''}`,
      value: contact.id,
    }))
  }, [contacts])

  // Auto-select logic for contacts
  useEffect(() => {
    if (!companyId || contacts.length === 0 || useManualEntry) return

    // Check for starred contact
    const starredContacts = contacts.filter((c: ContactResponse) => c.starred)
    if (starredContacts.length === 1) {
      handleSelectContact(starredContacts[0])
      setValue('selected_contact_id', starredContacts[0].id)
      return
    }

    // If exactly one contact total, auto-select it
    if (contacts.length === 1) {
      handleSelectContact(contacts[0])
      setValue('selected_contact_id', contacts[0].id)
    }
  }, [contacts, companyId, useManualEntry])

  const handleSelectContact = (contact: ContactResponse) => {
    setSelectedContactIdForDropdown(contact.id)
    setValue('contact_id', contact.id, { shouldValidate: true })
    setValue('contact_first_name', contact.first_name || '', {
      shouldValidate: true,
    })
    setValue('contact_last_name', contact.last_name || '', {
      shouldValidate: true,
    })
    setValue(
      'contact_email',
      contact.email_preferred ||
        contact.email_work ||
        contact.email_personal ||
        '',
      { shouldValidate: true }
    )
    setValue('contact_phone', contact.phone_number || '', {
      shouldValidate: true,
    })
    setUseManualEntry(false)
  }

  const handleContactChange = (contactId: number | null) => {
    if (!contactId) {
      setSelectedContactIdForDropdown(null)
      // Clear contact fields when no contact is selected
      setValue('contact_id', null, { shouldValidate: true })
      setValue('contact_first_name', '', { shouldValidate: true })
      setValue('contact_last_name', '', { shouldValidate: true })
      setValue('contact_email', '', { shouldValidate: true })
      setValue('contact_phone', '', { shouldValidate: true })
      return
    }
    const contact = contacts.find((c: ContactResponse) => c.id === contactId)
    if (contact) {
      handleSelectContact(contact)
    }
  }

  const handleSwitchToManual = () => {
    setUseManualEntry(true)
    setSelectedContactIdForDropdown(null)
    lastCheckedEmail.current = ''
    setEmailExists(false)
    // Clear contact fields when switching to manual
    setValue('contact_id', null, { shouldValidate: false })
    setValue('contact_first_name', '', { shouldValidate: false })
    setValue('contact_last_name', '', { shouldValidate: false })
    setValue('contact_email', '', { shouldValidate: false })
    setValue('contact_phone', '', { shouldValidate: false })
    setValue('selected_contact_id', null, { shouldValidate: false })
  }

  /**
   * Validate email uniqueness on blur by calling the check-email endpoint.
   * Only runs in manual entry mode; skips if the email hasn't changed since
   * the last check or fails basic format validation.
   */
  const handleEmailBlur = useCallback(
    async (email: string) => {
      const trimmed = email.trim().toLowerCase()
      if (!trimmed || trimmed === lastCheckedEmail.current) return
      lastCheckedEmail.current = trimmed

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return

      setIsCheckingEmail(true)
      try {
        const result = await checkContactEmail(trimmed, companyId || undefined)
        // Guard against stale response if user changed email while awaiting
        const currentEmail = (getValues('contact_email') || '')
          .trim()
          .toLowerCase()
        if (trimmed !== currentEmail) return
        setEmailExists(result.same_company || result.other_company)
        if (result.same_company) {
          setError('contact_email', {
            type: 'manual',
            message: 'A contact with this email already exists',
          })
        } else if (result.other_company) {
          setError('contact_email', {
            type: 'manual',
            message:
              'A contact with this email already exists for another company',
          })
        } else {
          clearErrors('contact_email')
        }
      } catch {
        // Silently fail — the backend will catch it on submit
      } finally {
        setIsCheckingEmail(false)
      }
    },
    [setError, clearErrors, companyId]
  )

  const handleSwitchToExisting = () => {
    setUseManualEntry(false)
    setSelectedContactIdForDropdown(null)
    lastCheckedEmail.current = ''
    setEmailExists(false)
    clearErrors('contact_email')
    // Clear contact fields when switching to existing mode without a selected contact
    setValue('contact_id', null, { shouldValidate: true })
    setValue('contact_first_name', '', { shouldValidate: true })
    setValue('contact_last_name', '', { shouldValidate: true })
    setValue('contact_email', '', { shouldValidate: true })
    setValue('contact_phone', '', { shouldValidate: true })
    setValue('selected_contact_id', null, { shouldValidate: false })
  }

  const handleSelectCompany = (
    company: ContactCompanySearchResult & {
      chain_id?: number
      address_line1?: string | null
      zipcode?: string | null
    },
    door?: DoorSearchResult
  ) => {
    const companyId = company.contact_company_id || 0
    const chainId = company.chain_id || null

    // Either contact_company_id or chain_id must be provided
    if (!companyId && !chainId) {
      return
    }

    setSelectedCompanyData(company)

    // Set both values without validating individually to avoid validation errors
    // when contact_company_id is 0 but chain_id is present
    setValue('contact_company_id', companyId, { shouldValidate: false })
    setValue('chain_id', chainId, { shouldValidate: false })

    if (door) {
      setSelectedDoorData(door)
      // Only set door_proxy_id if it exists (some chain types like DI/HO/CU/NH don't have door proxies)
      setValue('door_proxy_id', door.door_proxy_id || null, {
        shouldValidate: false,
      })
    } else {
      setSelectedDoorData(null)
      setValue('door_proxy_id', null, { shouldValidate: false })
    }

    // Prefill shipping address - prefer door's address over company's address
    // Door address is more specific when available
    const addressSource = door?.address ? door : company
    if (door?.address || company.address_line1) {
      setValue(
        'shipping_address_line1',
        door?.address || company.address_line1 || '',
        { shouldValidate: false }
      )
    }
    if (addressSource.city) {
      setValue('shipping_city', addressSource.city, { shouldValidate: false })
    }
    if (addressSource.state) {
      setValue('shipping_state', addressSource.state, { shouldValidate: false })
    }
    if (addressSource.zipcode) {
      setValue('shipping_zip', addressSource.zipcode, { shouldValidate: false })
    }

    // Trigger validation after all values are set (both company and address fields)
    trigger([
      'contact_company_id',
      'chain_id',
      'shipping_address_line1',
      'shipping_city',
      'shipping_state',
      'shipping_zip',
    ])
  }

  const handleClearCompany = () => {
    setSelectedCompanyData(null)
    setSelectedDoorData(null)
    setValue('contact_company_id', 0, { shouldValidate: false })
    setValue('chain_id', null, { shouldValidate: false })
    setValue('door_proxy_id', null, { shouldValidate: false })
    trigger(['contact_company_id', 'chain_id'])
  }

  return (
    <div>
      <h5 className="text-lg font-medium mb-2">
        Company & Contact Information
      </h5>

      <div className="mb-6">
        <Label>{selectedDoorData ? 'Company & Location *' : 'Company *'}</Label>
        {selectedCompanyData ? (
          <>
            <CompanyDisplay>
              <CompanyInfo>
                <CompanyName>{selectedCompanyData.name}</CompanyName>
                {!selectedDoorData &&
                  (selectedCompanyData.city || selectedCompanyData.state) && (
                    <CompanyDetails>
                      {selectedCompanyData.city && selectedCompanyData.state
                        ? `${selectedCompanyData.city}, ${selectedCompanyData.state}`
                        : selectedCompanyData.city || selectedCompanyData.state}
                    </CompanyDetails>
                  )}
                {selectedDoorData && (
                  <CompanyDetails className="mt-2 font-semibold">
                    📍 {selectedDoorData.name}
                    {selectedDoorData.address && (
                      <div className="font-normal mt-1">
                        {selectedDoorData.address}
                        {(selectedDoorData.city || selectedDoorData.state) && (
                          <>
                            {', '}
                            {selectedDoorData.city}
                            {selectedDoorData.city &&
                              selectedDoorData.state &&
                              ', '}
                            {selectedDoorData.state}
                          </>
                        )}
                      </div>
                    )}
                  </CompanyDetails>
                )}
              </CompanyInfo>
              <FbButton
                type="button"
                variant="danger-outline"
                size="sm"
                onClick={handleClearCompany}
                className="p-2 text-sm"
              >
                <FaTimes />
              </FbButton>
            </CompanyDisplay>
          </>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowCompanySearch(true)}
            className={cn(
              'flex items-center gap-2 border-fb-dark-green text-fb-dark-green',
              'hover:bg-fb-dark-green hover:text-white hover:border-fb-dark-green',
              'focus:shadow-[0_0_0_0.2rem_rgba(44,107,78,0.25)]'
            )}
          >
            <PiMagnifyingGlass />
            Search for Company or Location
          </Button>
        )}
        {errors.contact_company_id && (
          <ErrorText>{errors.contact_company_id.message as string}</ErrorText>
        )}
        <Controller
          name="contact_company_id"
          control={control}
          render={({ field }) => (
            <input type="hidden" tabIndex={-1} {...field} />
          )}
        />
        <Controller
          name="chain_id"
          control={control}
          render={({ field }) => (
            <input
              type="hidden"
              tabIndex={-1}
              {...field}
              value={field.value || ''}
            />
          )}
        />
      </div>

      {/* Contact Selection or Manual Entry - Only show when company is selected */}
      {hasCompanySelected && (
        <>
          {isLoadingContacts && !useManualEntry ? (
            // Show skeleton while loading contacts
            <div>
              <SkeletonLabel />
              <SkeletonInput />
            </div>
          ) : (
            <div>
              {/* Toggle Button Group - Always show */}
              <div className="mb-4">
                <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-lg w-fit">
                  <FbButton
                    type="button"
                    variant={!useManualEntry ? 'primary' : 'transparent'}
                    size="sm"
                    onClick={handleSwitchToExisting}
                    className="flex items-center gap-2 min-w-0"
                  >
                    <PiMagnifyingGlass />
                    Select Existing
                  </FbButton>
                  <FbButton
                    type="button"
                    variant={useManualEntry ? 'primary' : 'transparent'}
                    size="sm"
                    onClick={handleSwitchToManual}
                    className="flex items-center gap-2 min-w-0"
                  >
                    <FaEdit />
                    Enter Manually
                  </FbButton>
                </div>
              </div>

              {/* Contact Selection Mode */}
              {!useManualEntry && (
                <div className="mb-4">
                  <div className="flex flex-col mt-3">
                    <label className="text-base font-medium text-gray-800 select-none mb-1">
                      Select Contact *
                    </label>
                    <Controller
                      name="selected_contact_id"
                      control={control}
                      render={({ field }) => (
                        <>
                          <ReactSelectWrapper
                            {...field}
                            onChange={(newValue: any) => {
                              const contactId = newValue?.value || null
                              field.onChange(contactId)
                              handleContactChange(contactId)
                            }}
                            options={contactOptions}
                            isLoading={isLoadingContacts}
                            placeholder={
                              contacts.length === 0
                                ? 'No contacts available - switch to manual entry'
                                : 'Select a contact...'
                            }
                            value={contactOptions.find(
                              (opt) => opt.value === field.value
                            )}
                            isClearable
                            classNames={{
                              control: () =>
                                `${errors.contact_first_name || errors.contact_last_name || errors.contact_email ? 'input-error' : ''}`,
                            }}
                          />
                          {/* Show error if contact name or email has validation error */}
                          {(errors.contact_first_name ||
                            errors.contact_last_name ||
                            errors.contact_email) && (
                            <ErrorText>
                              Please select a contact or switch to manual entry
                            </ErrorText>
                          )}
                        </>
                      )}
                    />
                  </div>
                </div>
              )}

              {/* Manual Entry Mode */}
              {useManualEntry && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <Label>First Name *</Label>
                      <Controller
                        name="contact_first_name"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Form.Control
                              type="text"
                              {...field}
                              isInvalid={!!errors.contact_first_name}
                              placeholder="First name"
                            />
                            {errors.contact_first_name && (
                              <ErrorText>
                                {errors.contact_first_name.message as string}
                              </ErrorText>
                            )}
                          </>
                        )}
                      />
                    </div>

                    <div>
                      <Label>Last Name *</Label>
                      <Controller
                        name="contact_last_name"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Form.Control
                              type="text"
                              {...field}
                              isInvalid={!!errors.contact_last_name}
                              placeholder="Last name"
                            />
                            {errors.contact_last_name && (
                              <ErrorText>
                                {errors.contact_last_name.message as string}
                              </ErrorText>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <Label>Contact Email *</Label>
                      <Controller
                        name="contact_email"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Form.Control
                              type="email"
                              {...field}
                              isInvalid={!!errors.contact_email}
                              placeholder="Contact email"
                              onBlur={(
                                e: React.FocusEvent<HTMLInputElement>
                              ) => {
                                field.onBlur()
                                handleEmailBlur(e.target.value)
                              }}
                            />
                            {isCheckingEmail && (
                              <div className="text-xs text-fg-secondary mt-1">
                                Checking email...
                              </div>
                            )}
                            {errors.contact_email && (
                              <ErrorText>
                                {errors.contact_email.message as string}
                              </ErrorText>
                            )}
                          </>
                        )}
                      />
                    </div>

                    <PhoneInput
                      name="contact_phone"
                      control={control}
                      label="Contact Phone"
                      optional
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      <hr className="my-6" />

      <h5 className="text-lg font-medium mb-4">Shipping Information</h5>

      <div className="mb-6">
        <Label>Address Line 1 *</Label>
        <Controller
          name="shipping_address_line1"
          control={control}
          render={({ field }) => (
            <>
              <Form.Control
                type="text"
                {...field}
                isInvalid={!!errors.shipping_address_line1}
                placeholder="Street address"
              />
              {errors.shipping_address_line1 && (
                <ErrorText>
                  {errors.shipping_address_line1.message as string}
                </ErrorText>
              )}
            </>
          )}
        />
      </div>

      <div className="mb-6">
        <Label>Address Line 2</Label>
        <Controller
          name="shipping_address_line2"
          control={control}
          render={({ field }) => (
            <Form.Control
              type="text"
              {...field}
              placeholder="Apt, suite, unit, etc. (optional)"
            />
          )}
        />
      </div>

      <div className="flex gap-2 w-full max-w-full mt-3">
        <Select
          className="flex-1 mt-0"
          control={control}
          name="shipping_state"
          placeholder="Enter state"
          options={stateOptions}
          label="State"
          optional={false}
        />
        {/* City uses placeholder guard instead of disabled prop — disabled
            broke Tab navigation due to react-hook-form watch() re-render timing.
            cityOptions are derived from a local constant (no async loading). */}
        <CreatableSelect
          className="flex-1 mt-0"
          control={control}
          name="shipping_city"
          placeholder={watchState ? 'Enter city' : 'Select state first'}
          options={cityOptions}
          label="City"
          optional={false}
          onCreateOption={async (city) => {
            return { label: city, value: city }
          }}
        />
      </div>

      <div className="flex gap-2 w-full max-w-full mt-3 mb-6">
        <TextInput
          label="ZIP/Postal code"
          name="shipping_zip"
          control={control}
          placeholder="Enter ZIP/Postal code"
          optional={false}
          maxLength={5}
          type="text"
        />
      </div>

      {/* Company Search Modal */}
      <CompanySearchModal
        open={showCompanySearch}
        onClose={() => setShowCompanySearch(false)}
        onSelectCompany={handleSelectCompany}
      />
    </div>
  )
}
